import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../../apiClient'
import { getAverageTrend, getTrend } from './reportTrend'

type CurrentAndPrevious<T> = { current: T; previous: T }

type TaskOutcomeSourceReport = {
  programId: string
  taskCount: CurrentAndPrevious<number>
  auditedTaskCount: CurrentAndPrevious<number>
  avgAccuracy: CurrentAndPrevious<number | null>
  breakdown: Record<string, { auditedTaskCount: number; avgAccuracy: number | null }>
}

export type ProgramInstanceTaskOutcomeResponse = {
  programInstanceId: string
  targetAccuracy: number | null
  live: TaskOutcomeSourceReport
  recycler: TaskOutcomeSourceReport | null
}

type Granularity = 'daily' | 'weekly' | 'monthly'

export type TaskOutcomeMetric<T = number> = { value: T; trend: number }

/**
 * One chart point. `start` and `end` are breakdown bucket keys; they differ only in the weekly view,
 * where seven daily buckets are combined into one point.
 */
export type TaskAccuracyBucket = {
  start: string
  end: string
  live: number | null
  recycled: number | null
}

export type ProgramInstanceTaskOutcome = {
  targetAccuracy: number | null
  hasRecycler: boolean
  tasks: TaskOutcomeMetric
  auditedTasksRatio: TaskOutcomeMetric
  auditAvgAccuracy: TaskOutcomeMetric<number | null>
  recycledTasks: TaskOutcomeMetric
  recycledAvgAccuracy: TaskOutcomeMetric<number | null>
  accuracyBuckets: TaskAccuracyBucket[]
}

const DAYS_PER_WEEK = 7

export const PROGRAM_TASK_OUTCOME_STATS_KEY = 'program-task-outcome-stats'

function getDailyBuckets(response: ProgramInstanceTaskOutcomeResponse): TaskAccuracyBucket[] {
  return Object.keys(response.live.breakdown)
    .sort((a, b) => a.localeCompare(b))
    .map((timeslice) => ({
      start: timeslice,
      end: timeslice,
      live: response.live.breakdown[timeslice].avgAccuracy,
      recycled: response.recycler?.breakdown[timeslice]?.avgAccuracy ?? null,
    }))
}

/**
 * Keeps the weekly chart formula: the sum of seven daily accuracies divided by seven, counting a day
 * without samples as zero. A week without any sample in a source is a gap instead of 0%.
 *
 * @returns The weekly accuracy, or `null` when no day of the week has samples.
 */
function getWeeklyAccuracy(values: (number | null)[]) {
  const measured = values.filter((value): value is number => value !== null)
  if (measured.length === 0) return null

  const sum = measured.reduce((acc, value) => acc + value, 0)
  return Number.parseFloat((sum / DAYS_PER_WEEK).toFixed(2))
}

function groupBucketsByWeek(dailyBuckets: TaskAccuracyBucket[]): TaskAccuracyBucket[] {
  const weeks: TaskAccuracyBucket[] = []

  for (let i = 0; i < dailyBuckets.length; i += DAYS_PER_WEEK) {
    const week = dailyBuckets.slice(i, i + DAYS_PER_WEEK)
    weeks.push({
      start: week[0].start,
      end: week.at(-1)?.end ?? week[0].end,
      live: getWeeklyAccuracy(week.map((day) => day.live)),
      recycled: getWeeklyAccuracy(week.map((day) => day.recycled)),
    })
  }

  return weeks
}

export function toProgramInstanceTaskOutcome(
  response: ProgramInstanceTaskOutcomeResponse,
  granularity?: Granularity
): ProgramInstanceTaskOutcome {
  const { live, recycler } = response
  const dailyBuckets = getDailyBuckets(response)

  return {
    targetAccuracy: response.targetAccuracy,
    hasRecycler: recycler !== null,
    tasks: {
      value: live.taskCount.current,
      trend: getTrend(live.taskCount.current, live.taskCount.previous),
    },
    auditedTasksRatio: {
      value: live.auditedTaskCount.current / (live.taskCount.current || 1),
      trend: getTrend(live.auditedTaskCount.current, live.auditedTaskCount.previous),
    },
    auditAvgAccuracy: {
      value: live.avgAccuracy.current,
      trend: getAverageTrend(live.avgAccuracy),
    },
    recycledTasks: {
      value: recycler?.taskCount.current ?? 0,
      trend: getTrend(recycler?.taskCount.current, recycler?.taskCount.previous),
    },
    recycledAvgAccuracy: {
      value: recycler?.avgAccuracy.current ?? null,
      trend: recycler ? getAverageTrend(recycler.avgAccuracy) : 0,
    },
    accuracyBuckets: granularity === 'weekly' ? groupBucketsByWeek(dailyBuckets) : dailyBuckets,
  }
}

export function useGetTaskOutcome(
  programInstanceId?: string,
  startDate?: string,
  endDate?: string,
  granularity?: Granularity
) {
  return useQuery({
    queryKey: [PROGRAM_TASK_OUTCOME_STATS_KEY, programInstanceId, startDate, endDate, granularity],
    queryFn: async () => {
      const { data } = await apiClient.get<ProgramInstanceTaskOutcomeResponse>(
        `/int/v3/program-instances/${programInstanceId}/reports/task-outcome`,
        { params: { startDate, endDate, granularity } }
      )

      return data
    },
    select: (data) => toProgramInstanceTaskOutcome(data, granularity),
    enabled: Boolean(programInstanceId) && Boolean(startDate) && Boolean(endDate) && Boolean(granularity),
    refetchOnWindowFocus: false,
  })
}
